import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  Flag,
  Send,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Anti-cheat component
const AntiCheat = ({ attemptId, userId, onViolation, enabled = true }) => {
  const violationCount = useRef(0);
  
  useEffect(() => {
    if (!enabled) return;
    
    const logViolation = async (type, details = '') => {
      violationCount.current += 1;
      
      try {
        const response = await fetch(`${API_URL}/api/exams/proctoring/log?user_id=${userId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attempt_id: attemptId,
            violation_type: type,
            details,
          }),
        });
        
        const result = await response.json();
        onViolation(result);
      } catch (error) {
        console.error('Failed to log violation:', error);
      }
    };
    
    // Tab/window visibility change
    const handleVisibilityChange = () => {
      if (document.hidden) {
        logViolation('tab_switch', 'User switched tabs');
        toast.warning('Avertissement: Vous avez quitté l\'onglet de l\'examen');
      }
    };
    
    // Window blur
    const handleBlur = () => {
      logViolation('window_blur', 'Window lost focus');
    };
    
    // Context menu (right click)
    const handleContextMenu = (e) => {
      e.preventDefault();
      logViolation('right_click', 'Right-click attempted');
      toast.warning('Clic droit désactivé pendant l\'examen');
    };
    
    // Copy/paste prevention
    const handleCopyPaste = (e) => {
      e.preventDefault();
      logViolation('copy_paste', `${e.type} attempted`);
      toast.warning('Copier/Coller désactivé pendant l\'examen');
    };
    
    // Keyboard shortcuts
    const handleKeyDown = (e) => {
      // Block Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+P
      if (e.ctrlKey && ['c', 'v', 'x', 'p'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        logViolation('keyboard_shortcut', `Ctrl+${e.key.toUpperCase()} attempted`);
        toast.warning('Raccourci clavier désactivé');
      }
      
      // Block F12
      if (e.key === 'F12') {
        e.preventDefault();
        logViolation('keyboard_shortcut', 'F12 attempted');
      }
    };
    
    // Fullscreen exit
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        logViolation('fullscreen_exit', 'Exited fullscreen');
        toast.warning('Mode plein écran recommandé');
      }
    };
    
    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('copy', handleCopyPaste);
    document.addEventListener('cut', handleCopyPaste);
    document.addEventListener('paste', handleCopyPaste);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopyPaste);
      document.removeEventListener('cut', handleCopyPaste);
      document.removeEventListener('paste', handleCopyPaste);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [attemptId, userId, enabled, onViolation]);
  
  return null;
};

// Timer component
const ExamTimer = ({ seconds, onTimeUp }) => {
  const [timeLeft, setTimeLeft] = useState(seconds);
  
  useEffect(() => {
    setTimeLeft(seconds);
  }, [seconds]);
  
  useEffect(() => {
    if (timeLeft <= 0) {
      onTimeUp();
      return;
    }
    
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [timeLeft, onTimeUp]);
  
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const secs = timeLeft % 60;
  
  const isLow = timeLeft < 300; // Less than 5 minutes
  const isCritical = timeLeft < 60; // Less than 1 minute
  
  return (
    <div className={cn(
      "flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-lg",
      isCritical ? "bg-red-500 text-white animate-pulse" :
      isLow ? "bg-yellow-500 text-white" :
      "bg-muted"
    )}>
      <Clock className="h-5 w-5" />
      <span>
        {hours > 0 && `${hours.toString().padStart(2, '0')}:`}
        {minutes.toString().padStart(2, '0')}:{secs.toString().padStart(2, '0')}
      </span>
    </div>
  );
};

// Question renderer component
const QuestionRenderer = ({ question, answer, onChange, showCorrect = false }) => {
  const renderChoices = () => {
    if (!question.choices) return null;
    
    if (question.type === 'single_choice' || question.type === 'true_false') {
      return (
        <RadioGroup
          value={answer || ''}
          onValueChange={onChange}
          className="space-y-3"
        >
          {question.choices.map((choice) => (
            <div
              key={choice.id}
              className={cn(
                "flex items-center space-x-3 p-4 rounded-lg border transition-colors",
                answer === choice.id ? "border-primary bg-primary/5" : "hover:bg-muted/50"
              )}
            >
              <RadioGroupItem value={choice.id} id={choice.id} />
              <Label htmlFor={choice.id} className="flex-1 cursor-pointer">
                {choice.text}
              </Label>
            </div>
          ))}
        </RadioGroup>
      );
    }
    
    if (question.type === 'multiple_choice') {
      const selectedIds = Array.isArray(answer) ? answer : [];
      
      return (
        <div className="space-y-3">
          {question.choices.map((choice) => (
            <div
              key={choice.id}
              className={cn(
                "flex items-center space-x-3 p-4 rounded-lg border transition-colors",
                selectedIds.includes(choice.id) ? "border-primary bg-primary/5" : "hover:bg-muted/50"
              )}
            >
              <Checkbox
                id={choice.id}
                checked={selectedIds.includes(choice.id)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    onChange([...selectedIds, choice.id]);
                  } else {
                    onChange(selectedIds.filter((id) => id !== choice.id));
                  }
                }}
              />
              <Label htmlFor={choice.id} className="flex-1 cursor-pointer">
                {choice.text}
              </Label>
            </div>
          ))}
        </div>
      );
    }
    
    return null;
  };
  
  const renderShortAnswer = () => (
    <Input
      placeholder="Votre réponse..."
      value={answer || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full"
      data-testid="short-answer-input"
    />
  );
  
  const renderLongAnswer = () => (
    <Textarea
      placeholder="Rédigez votre réponse..."
      value={answer || ''}
      onChange={(e) => onChange(e.target.value)}
      rows={6}
      className="w-full resize-none"
      data-testid="long-answer-input"
    />
  );
  
  const renderMatching = () => {
    const pairs = question.matching_pairs || [];
    const currentAnswer = typeof answer === 'object' ? answer : {};
    
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Associez chaque élément de gauche avec son correspondant
        </p>
        {pairs.map((pair, index) => (
          <div key={index} className="flex items-center gap-4">
            <div className="flex-1 p-3 bg-muted rounded-lg">
              {pair.left}
            </div>
            <span className="text-muted-foreground">→</span>
            <select
              className="flex-1 p-3 border rounded-lg bg-background"
              value={currentAnswer[pair.left] || ''}
              onChange={(e) => onChange({ ...currentAnswer, [pair.left]: e.target.value })}
            >
              <option value="">Sélectionner...</option>
              {pairs.map((p, i) => (
                <option key={i} value={p.right}>{p.right}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
    );
  };
  
  const renderOrdering = () => {
    const items = question.correct_order || [];
    const currentOrder = Array.isArray(answer) ? answer : [...items];
    
    const moveItem = (fromIndex, toIndex) => {
      const newOrder = [...currentOrder];
      const [item] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, item);
      onChange(newOrder);
    };
    
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground mb-4">
          Réorganisez les éléments dans le bon ordre (glissez-déposez)
        </p>
        {currentOrder.map((item, index) => (
          <div
            key={index}
            className="flex items-center gap-3 p-3 bg-muted rounded-lg cursor-move"
            draggable
            onDragStart={(e) => e.dataTransfer.setData('index', index.toString())}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const fromIndex = parseInt(e.dataTransfer.getData('index'));
              moveItem(fromIndex, index);
            }}
          >
            <span className="text-muted-foreground font-medium w-6">{index + 1}.</span>
            <span className="flex-1">{item}</span>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={index === 0}
                onClick={() => moveItem(index, index - 1)}
              >
                ↑
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={index === currentOrder.length - 1}
                onClick={() => moveItem(index, index + 1)}
              >
                ↓
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <div className="space-y-6">
      <div className="prose prose-lg max-w-none">
        <div dangerouslySetInnerHTML={{ __html: question.question_text }} />
      </div>
      
      {question.type === 'single_choice' && renderChoices()}
      {question.type === 'multiple_choice' && renderChoices()}
      {question.type === 'true_false' && renderChoices()}
      {question.type === 'short_answer' && renderShortAnswer()}
      {question.type === 'long_answer' && renderLongAnswer()}
      {question.type === 'matching' && renderMatching()}
      {question.type === 'ordering' && renderOrdering()}
    </div>
  );
};

export default function ExamTakePage() {
  const { workspaceId, examId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [exam, setExam] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [markedForReview, setMarkedForReview] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [violations, setViolations] = useState(0);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showNav, setShowNav] = useState(true);
  
  const userId = user?.id;
  
  // Auto-save interval
  const autoSaveRef = useRef(null);
  const answersRef = useRef({});
  const submittingRef = useRef(false);
  
  // localStorage helper keys
  const LS_ANSWERS_KEY = `exam_answers_int_${examId}_${userId}`;
  const LS_INDEX_KEY = `exam_index_int_${examId}_${userId}`;
  const LS_REVIEW_KEY = `exam_review_int_${examId}_${userId}`;
  
  // Keep answersRef in sync
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);
  
  // Save to localStorage on every answer change
  useEffect(() => {
    if (!examId || !userId) return;
    try {
      localStorage.setItem(LS_ANSWERS_KEY, JSON.stringify(answers));
    } catch (e) {}
  }, [answers, examId, userId]);
  
  // Save currentIndex to localStorage
  useEffect(() => {
    if (!examId || !userId) return;
    try {
      localStorage.setItem(LS_INDEX_KEY, String(currentIndex));
    } catch (e) {}
  }, [currentIndex, examId, userId]);
  
  // Save markedForReview to localStorage
  useEffect(() => {
    if (!examId || !userId) return;
    try {
      localStorage.setItem(LS_REVIEW_KEY, JSON.stringify(markedForReview));
    } catch (e) {}
  }, [markedForReview, examId, userId]);
  
  // Warn before page unload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (Object.keys(answersRef.current).length > 0 && !submittingRef.current) {
        e.preventDefault();
        e.returnValue = 'Vos réponses non soumises seront sauvegardées.';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);
  
  // Cleanup localStorage helper
  const clearLocalStorage = () => {
    try {
      localStorage.removeItem(LS_ANSWERS_KEY);
      localStorage.removeItem(LS_INDEX_KEY);
      localStorage.removeItem(LS_REVIEW_KEY);
    } catch (e) {}
  };
  

  useEffect(() => {
    if (examId && userId && workspaceId) {
      startExam();
    }
    
    return () => {
      if (autoSaveRef.current) {
        clearInterval(autoSaveRef.current);
      }
    };
  }, [examId, userId, workspaceId]);
  
  // Setup auto-save
  useEffect(() => {
    if (attempt) {
      autoSaveRef.current = setInterval(() => {
        saveAnswers(false);
      }, 30000); // Auto-save every 30 seconds
      
      return () => {
        if (autoSaveRef.current) {
          clearInterval(autoSaveRef.current);
        }
      };
    }
  }, [attempt, answers]);
  
  const startExam = async () => {
    try {
      setLoading(true);
      
      // Start or resume attempt
      const attemptResponse = await fetch(
        `${API_URL}/api/exams/attempts/start?workspace_id=${workspaceId}&user_id=${userId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ exam_id: examId }),
        }
      );
      
      if (!attemptResponse.ok) {
        const error = await attemptResponse.json();
        toast.error(error.detail || 'Impossible de démarrer l\'examen');
        navigate(`/workspace/${workspaceId}/exams`);
        return;
      }
      
      const attemptData = await attemptResponse.json();
      setAttempt(attemptData);
      setTimeRemaining(attemptData.time_remaining_seconds || 3600);
      setViolations(attemptData.violations_count || 0);
      
      // Get exam details
      const examResponse = await fetch(
        `${API_URL}/api/exams/${examId}?workspace_id=${workspaceId}&include_questions=false`
      );
      const examData = await examResponse.json();
      setExam(examData);
      
      // Get questions (without correct answers)
      const questionsResponse = await fetch(
        `${API_URL}/api/exams/attempts/${attemptData.id}/questions?user_id=${userId}`
      );
      const questionsData = await questionsResponse.json();
      setQuestions(questionsData.questions || []);
      
      // Merge answers from localStorage (more recent) over server answers
      const serverAnswers = questionsData.answers || {};
      let mergedAnswers = { ...serverAnswers };
      try {
        const savedAnswers = localStorage.getItem(LS_ANSWERS_KEY);
        if (savedAnswers) {
          const parsed = JSON.parse(savedAnswers);
          mergedAnswers = { ...serverAnswers, ...parsed };
        }
      } catch (e) {}
      setAnswers(mergedAnswers);
      
      // Restore markedForReview from localStorage or server
      try {
        const savedReview = localStorage.getItem(LS_REVIEW_KEY);
        if (savedReview) {
          setMarkedForReview(JSON.parse(savedReview));
        } else {
          setMarkedForReview(questionsData.marked_for_review || []);
        }
      } catch (e) {
        setMarkedForReview(questionsData.marked_for_review || []);
      }
      
      // Restore currentIndex from localStorage
      try {
        const savedIndex = localStorage.getItem(LS_INDEX_KEY);
        if (savedIndex !== null) {
          const idx = parseInt(savedIndex, 10);
          if (!isNaN(idx) && idx >= 0 && idx < (questionsData.questions || []).length) {
            setCurrentIndex(idx);
          } else {
            setCurrentIndex(questionsData.current_index || 0);
          }
        } else {
          setCurrentIndex(questionsData.current_index || 0);
        }
      } catch (e) {
        setCurrentIndex(questionsData.current_index || 0);
      }
      
      // Request fullscreen
      try {
        await document.documentElement.requestFullscreen();
      } catch (e) {
        console.log('Fullscreen not supported');
      }
      
    } catch (error) {
      console.error('Failed to start exam:', error);
      toast.error('Erreur lors du démarrage de l\'examen');
      navigate(`/workspace/${workspaceId}/exams`);
    } finally {
      setLoading(false);
    }
  };
  
  const saveAnswers = async (showToast = false) => {
    if (!attempt) return;
    
    try {
      const answersToSave = Object.entries(answers).map(([questionId, answer]) => ({
        question_id: questionId,
        answer,
      }));
      
      await fetch(`${API_URL}/api/exams/attempts/${attempt.id}/save?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: answersToSave, is_final: false }),
      });
      
      if (showToast) {
        toast.success('Réponses sauvegardées');
      }
    } catch (error) {
      console.error('Failed to save answers:', error);
    }
  };
  
  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };
  
  const toggleMarkForReview = async (questionId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/exams/attempts/${attempt.id}/mark-review?question_id=${questionId}&user_id=${userId}`,
        { method: 'POST' }
      );
      const data = await response.json();
      setMarkedForReview(data.marked_for_review || []);
    } catch (error) {
      console.error('Failed to mark for review:', error);
    }
  };
  
  const handleSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    
    const currentAnswers = answersRef.current;
    
    try {
      const answersToSubmit = Object.entries(currentAnswers).map(([questionId, answer]) => ({
        question_id: questionId,
        answer,
      }));
      
      const response = await fetch(
        `${API_URL}/api/exams/attempts/${attempt.id}/submit?user_id=${userId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers: answersToSubmit, is_final: true }),
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        
        // Exit fullscreen
        if (document.fullscreenElement) {
          document.exitFullscreen();
        }
        
        // Cleanup localStorage
        clearLocalStorage();
        
        // Navigate to results
        navigate(`/workspace/${workspaceId}/exams/results/${attempt.id}`, { state: { result } });
      } else {
        let errorMessage = 'Erreur lors de la soumission';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch (parseErr) {
          errorMessage = `Erreur serveur (${response.status})`;
        }
        
        if (response.status === 500) {
          toast.error(errorMessage, { duration: 8000 });
        } else if (response.status === 400) {
          // "Tentative déjà soumise" - try navigating to results directly
          toast.info('Votre examen a déjà été soumis, redirection vers les résultats...');
          clearLocalStorage();
          if (document.fullscreenElement) {
            document.exitFullscreen();
          }
          navigate(`/workspace/${workspaceId}/exams/results/${attempt.id}`);
          return;
        } else {
          toast.error(errorMessage);
        }
        submittingRef.current = false;
      }
    } catch (error) {
      console.error('Failed to submit:', error);
      toast.error('Erreur de connexion lors de la soumission. Vérifiez votre connexion et réessayez.');
      submittingRef.current = false;
    } finally {
      setSubmitting(false);
      setShowSubmitDialog(false);
    }
  };
  
  const handleTimeUp = useCallback(() => {
    if (submittingRef.current) return;
    toast.warning('Temps écoulé ! Soumission automatique...');
    handleSubmit();
  }, [attempt?.id, userId]);
  
  const handleViolation = (result) => {
    setViolations(result.violations_count);
    
    if (result.should_auto_submit) {
      toast.error('Trop d\'infractions détectées. Examen soumis automatiquement.');
      handleSubmit();
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement de l'examen...</p>
        </div>
      </div>
    );
  }
  
  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const answeredCount = Object.keys(answers).filter((k) => answers[k] !== null && answers[k] !== '' && (Array.isArray(answers[k]) ? answers[k].length > 0 : true)).length;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800" data-testid="exam-take-page">
      {/* Anti-cheat */}
      <AntiCheat
        attemptId={attempt?.id}
        userId={userId}
        onViolation={handleViolation}
        enabled={exam?.settings?.enable_proctoring}
      />
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="font-semibold truncate">{exam?.title}</h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>Question {currentIndex + 1} / {questions.length}</span>
                <span>•</span>
                <span>{answeredCount} répondues</span>
                {violations > 0 && (
                  <>
                    <span>•</span>
                    <Badge variant="destructive" className="text-xs">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      {violations} avertissement{violations > 1 ? 's' : ''}
                    </Badge>
                  </>
                )}
              </div>
            </div>
            
            <div data-testid="exam-timer">
              <ExamTimer seconds={timeRemaining} onTimeUp={handleTimeUp} />
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowNav(!showNav)}
              className="hidden md:flex"
            >
              {showNav ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          
          {/* Progress bar */}
          <Progress value={progress} className="mt-3 h-2" />
        </div>
      </header>
      
      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Main content */}
          <div className="flex-1">
            <Card className="shadow-lg">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-sm">
                    Question {currentIndex + 1}
                  </Badge>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {currentQuestion?.points || 1} point{(currentQuestion?.points || 1) > 1 ? 's' : ''}
                    </span>
                    <Button
                      variant={markedForReview.includes(currentQuestion?.id) ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleMarkForReview(currentQuestion?.id)}
                      data-testid="btn-mark-review"
                    >
                      <Flag className="h-4 w-4 mr-1" />
                      {markedForReview.includes(currentQuestion?.id) ? 'Marquée' : 'À revoir'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="py-8">
                {currentQuestion && (
                  <QuestionRenderer
                    question={currentQuestion}
                    answer={answers[currentQuestion.id]}
                    onChange={(value) => handleAnswerChange(currentQuestion.id, value)}
                  />
                )}
              </CardContent>
            </Card>
            
            {/* Navigation */}
            <div className="flex items-center justify-between mt-6">
              <Button
                variant="outline"
                onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
                disabled={currentIndex === 0}
                data-testid="btn-previous"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Précédent
              </Button>
              
              <Button variant="outline" onClick={() => saveAnswers(true)} data-testid="btn-save">
                Sauvegarder
              </Button>
              
              {currentIndex === questions.length - 1 ? (
                <Button onClick={() => setShowSubmitDialog(true)} data-testid="btn-submit-exam">
                  <Send className="h-4 w-4 mr-2" />
                  Soumettre l'examen
                </Button>
              ) : (
                <Button
                  onClick={() => setCurrentIndex((prev) => Math.min(questions.length - 1, prev + 1))}
                  data-testid="btn-next"
                >
                  Suivant
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
          
          {/* Question navigator */}
          {showNav && (
            <div className="hidden md:block w-64 shrink-0">
              <Card className="sticky top-24">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Navigation</CardTitle>
                </CardHeader>
                <CardContent className="pb-4">
                  <div className="grid grid-cols-5 gap-2">
                    {questions.map((q, index) => {
                      const isAnswered = answers[q.id] !== undefined && answers[q.id] !== '' && 
                        (Array.isArray(answers[q.id]) ? answers[q.id].length > 0 : true);
                      const isMarked = markedForReview.includes(q.id);
                      const isCurrent = index === currentIndex;
                      
                      return (
                        <button
                          key={q.id}
                          onClick={() => setCurrentIndex(index)}
                          className={cn(
                            "h-10 w-10 rounded-lg text-sm font-medium transition-all",
                            isCurrent && "ring-2 ring-primary ring-offset-2",
                            isAnswered && !isCurrent && "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
                            !isAnswered && !isCurrent && "bg-muted hover:bg-muted/80",
                            isMarked && "relative"
                          )}
                        >
                          {index + 1}
                          {isMarked && (
                            <Flag className="absolute -top-1 -right-1 h-3 w-3 text-orange-500" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="mt-4 space-y-2 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded bg-green-100 dark:bg-green-900" />
                      <span>Répondue</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded bg-muted" />
                      <span>Non répondue</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Flag className="h-4 w-4 text-orange-500" />
                      <span>À revoir</span>
                    </div>
                  </div>
                  
                  <Button
                    variant="destructive"
                    className="w-full mt-4"
                    onClick={() => setShowSubmitDialog(true)}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Soumettre
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
      
      {/* Submit confirmation dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la soumission</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>Êtes-vous sûr de vouloir soumettre votre examen ?</p>
                
                <div className="bg-muted p-4 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span>Questions répondues :</span>
                    <span className="font-medium">{answeredCount} / {questions.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Questions à revoir :</span>
                    <span className="font-medium">{markedForReview.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Questions sans réponse :</span>
                    <span className="font-medium text-orange-600">
                      {questions.length - answeredCount}
                    </span>
                  </div>
                </div>
                
                {questions.length - answeredCount > 0 && (
                  <p className="text-sm text-orange-600 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Attention : certaines questions n'ont pas de réponse
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Continuer l'examen</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Soumission...' : 'Confirmer la soumission'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
